// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { mergeWithCss } from "@ces/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useMemo, useState } from "react";

interface ProductGridContainerContextType {
    styles: ProductGridContainerStyle;
}
export interface ProductGridContainerStyle {
    container?: InjectableCss;
    info?: InjectableCss;
    addToList?: InjectableCss;
    price?: InjectableCss;
    availability?: InjectableCss;
    qtyInfo?: InjectableCss;
    addToCartActions?: InjectableCss;
    image?: InjectableCss;
    partNumbersAndBadges?: InjectableCss;
    priceAndActions?: InjectableCss;
}

export const productGridContainerStyle: ProductGridContainerStyle = {};

const ProductGridContainerContext = React.createContext<ProductGridContainerContextType>({
    styles: {},
});
const sharedClassName = "product-grid-container";

const enum NAMES {
    Image = "Image",
    Info = "Info",
    PartNumbersAndBadges = "PartNumbersAndBadges",
    Price = "Price",
    Availability = "Availability",
    QtyInfo = "QtyInfo",
    AddToCartActions = "AddToCartActions",
    AddToList = "AddToList",
    PriceAndActions = "PriceAndActions",
}
const getChild = (children: React.ReactElement[], childName: NAMES) =>
    children.find(child => (child.type as React.NamedExoticComponent<any>)?.displayName === childName);

const getDestructeredChildren = (childrenArray: React.ReactElement[]) => {
    return [
        getChild(childrenArray, NAMES.Image),
        getChild(childrenArray, NAMES.Info),
        getChild(childrenArray, NAMES.PartNumbersAndBadges),
        getChild(childrenArray, NAMES.Price),
        getChild(childrenArray, NAMES.Availability),
        getChild(childrenArray, NAMES.QtyInfo),
        getChild(childrenArray, NAMES.AddToCartActions),
        getChild(childrenArray, NAMES.PriceAndActions),
        getChild(childrenArray, NAMES.AddToList),
    ];
};
const ProductGridContainer = (props: {
    children: any;
    extendedStyles?: ProductGridContainerStyle;
    [key: string]: any;
}) => {
    const { children, extendedStyles, ...otherProps } = props;
    const [styles] = useState(() => mergeWithCss(productGridContainerStyle, extendedStyles));
    const value = useMemo(() => ({ styles }), [styles]);
    const childrenArray = React.Children.toArray(children) as React.ReactElement[];
    const [
        image,
        info,
        partNumbersAndBadges,
        price,
        availability,
        QtyInfo,
        addToCartActions,
        priceAndActions,
        addToList,
    ] = React.useMemo(() => getDestructeredChildren(childrenArray), [childrenArray]);
    return (
        <ProductGridContainerContext.Provider value={value}>
            <StyledWrapper {...styles.container} {...otherProps}>
                <StyledWrapper className="top-section">
                    {!!image && <StyledWrapper className="image-section">{image}</StyledWrapper>}
                    <StyledWrapper className="info-section">
                        {info}
                        {partNumbersAndBadges}
                        {price}
                        {availability}
                        {QtyInfo}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper className="price-and-actions-section">{priceAndActions}</StyledWrapper>
                <StyledWrapper className="bottom-section">
                    {addToCartActions}
                    {addToList}
                </StyledWrapper>
            </StyledWrapper>
        </ProductGridContainerContext.Provider>
    );
};

export const useProductGridContainerContext = () => {
    const context = React.useContext(ProductGridContainerContext);
    if (!context) {
        throw new Error("ProductGridTemplate compound components cannot be rendered outside ProductGridContainer");
    }
    return context;
};

const Image: FC = ({ children, ...otherProps }) => {
    const {
        styles: { image },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...image} className={`${sharedClassName}-image`}>
            {children}
        </StyledWrapper>
    );
};

const Info: FC = ({ children, ...otherProps }) => {
    const {
        styles: { info },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...info} className={`${sharedClassName}-info`}>
            {children}
        </StyledWrapper>
    );
};

const PartNumbersAndBadges: FC = ({ children, ...otherProps }) => {
    const {
        styles: { partNumbersAndBadges },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...partNumbersAndBadges} className={`${sharedClassName}-partNumbersAndBadges`}>
            {children}
        </StyledWrapper>
    );
};

const AddToCartActions: FC = ({ children, ...otherProps }) => {
    const {
        styles: { addToCartActions },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToCartActions} className={`${sharedClassName}-addToCartActions`}>
            {children}
        </StyledWrapper>
    );
};

const AddToList: FC = ({ children, ...otherProps }) => {
    const {
        styles: { addToList },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToList} data-test-selector={`${sharedClassName}-AddToListActions`}>
            {children}
        </StyledWrapper>
    );
};

const PriceAndActions: FC = ({ children, ...otherProps }) => {
    const {
        styles: { priceAndActions },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...priceAndActions} className={`${sharedClassName}-priceAndActions`}>
            {children}
        </StyledWrapper>
    );
};

const QtyInfo: FC = ({ children, ...otherProps }) => {
    const {
        styles: { qtyInfo },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...qtyInfo} className={`${sharedClassName}-qtyInfo`}>
            {children}
        </StyledWrapper>
    );
};

const Price: FC = ({ children, ...otherProps }) => {
    const {
        styles: { price },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...price} className={`${sharedClassName}-price`}>
            {children}
        </StyledWrapper>
    );
};

const Availability: FC = ({ children, ...otherProps }) => {
    const {
        styles: { availability },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...availability} className={`${sharedClassName}-availability`}>
            {children}
        </StyledWrapper>
    );
};

ProductGridContainer.Image = Image;
ProductGridContainer.Image.displayName = NAMES.Image;
ProductGridContainer.Info = Info;
ProductGridContainer.Info.displayName = NAMES.Info;
ProductGridContainer.PartNumbersAndBadges = PartNumbersAndBadges;
ProductGridContainer.PartNumbersAndBadges.displayName = NAMES.PartNumbersAndBadges;
ProductGridContainer.PriceAndActions = PriceAndActions;
ProductGridContainer.PriceAndActions.displayName = NAMES.PriceAndActions;
ProductGridContainer.Price = Price;
ProductGridContainer.Price.displayName = NAMES.Price;
ProductGridContainer.Availability = Availability;
ProductGridContainer.Availability.displayName = NAMES.Availability;
ProductGridContainer.QtyInfo = QtyInfo;
ProductGridContainer.QtyInfo.displayName = NAMES.QtyInfo;
ProductGridContainer.AddToCartActions = AddToCartActions;
ProductGridContainer.AddToCartActions.displayName = NAMES.AddToCartActions;
ProductGridContainer.AddToList = AddToList;
ProductGridContainer.AddToList.displayName = NAMES.AddToList;

export default ProductGridContainer;
